import React from "react";
import {
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  TerminalRounded as TerminalIcon,
  StorageRounded as StorageIcon,
  MemoryRounded as MemoryIcon,
  SecurityRounded as ShieldIcon,
  DataUsageRounded as DataUsageIcon,
  LanguageRounded as WebIcon,
  ExpandMoreRounded as ExpandMoreIcon,
  InfoOutlined as InfoIcon,
  WarningAmberRounded as WarningIcon,
  CheckCircleOutlineRounded as CheckCircleIcon,
  CancelOutlined as CancelIcon,
} from "@mui/icons-material";
import LogLine from "../LogLine";
import Pm2AppCard from "../Pm2AppCard";

// --- Akıllı Servis (Service) Açıklayıcısı ---
const getServiceDescription = (name) => {
  const n = name.toLowerCase();
  // Kritik Servisler
  if (n.includes("nginx"))
    return "Ters vekil (Reverse Proxy) ve yüksek performanslı web sunucusu.";
  if (n.includes("httpd") || n.includes("apache"))
    return "Apache HTTP web sunucusu.";
  if (n.includes("mysql") || n.includes("mariadb"))
    return "İlişkisel veritabanı motoru.";
  if (n.includes("docker")) return "Konteyner yönetim motoru.";
  if (n.includes("sshd")) return "Güvenli uzak terminal bağlantısı (SSH).";
  if (n.includes("cron")) return "Zamanlanmış görev yöneticisi.";
  // İşletim Sistemi Servisleri
  if (n.includes("network") || n.includes("netplan"))
    return "Ağ bağdaştırıcı yöneticisi.";
  if (n.includes("firewall") || n.includes("ufw") || n.includes("iptables"))
    return "Güvenlik duvarı servisi.";
  if (n.includes("systemd")) return "Çekirdek sistem yöneticisi.";
  if (n.includes("dbus")) return "Sistem içi mesajlaşma veriyolu.";
  if (n.includes("polkit"))
    return "Sistem yetkilendirme ve ayrıcalık yöneticisi.";
  if (n.includes("rsyslog") || n.includes("journal"))
    return "Sistem loglama servisi.";
  if (n.includes("snap")) return "Snap paket yöneticisi arka plan servisi.";
  return "Sistem altyapı arka plan servisi."; // Bilinmeyenler için default
};

// --- Akıllı Süreç (Process) Açıklayıcı Kartı ---
const ProcCard = ({ proc }) => {
  const getProcDescription = (name) => {
    const n = name.toLowerCase();
    if (n.includes("node")) return "Node.js arka plan çalışma zamanı.";
    if (n.includes("mysql") || n.includes("mariadb"))
      return "Veritabanı yönetim motoru.";
    if (n.includes("nginx") || n.includes("httpd") || n.includes("apache"))
      return "Web sunucusu ve ters vekil (proxy).";
    if (n.includes("pm2")) return "Uygulama süreç yöneticisi.";
    if (n.includes("php")) return "PHP yürütme motoru.";
    if (n.includes("java")) return "Java Sanal Makinesi (JVM).";
    if (n.includes("python")) return "Python yürütme ortamı.";
    if (n.includes("redis")) return "Bellek içi (In-memory) önbellekleme.";
    if (n.includes("docker") || n.includes("containerd"))
      return "Konteyner yönetim servisi.";
    if (n.includes("sshd")) return "Güvenli uzak bağlantı (SSH) servisi.";
    if (n.includes("systemd") || n.includes("kworker"))
      return "Çekirdek (Kernel) sistemi.";
    return "Standart işletim sistemi süreci.";
  };

  const isWarning = parseFloat(proc.cpu) > 60 || parseFloat(proc.mem) > 60;

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1,
        bgcolor: "rgba(234,240,248,0.03)",
        borderRadius: 2,
        borderLeft: "3px solid",
        borderColor: isWarning ? "warning.main" : "primary.main",
        "&:last-child": { mb: 0 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Typography sx={{ color: "text.primary", fontWeight: "bold" }} variant="caption">
          {proc.name.toUpperCase()}
        </Typography>
        <Typography sx={{ color: isWarning ? "warning.light" : "text.secondary", fontFamily: "monospace", fontWeight: "bold" }}
          variant="caption"
        >
          CPU: %{proc.cpu} | RAM: %{proc.mem}
        </Typography>
      </Box>
      <Typography sx={{ color: "text.disabled", display: "flex", alignItems: "center", gap: 0.5 }}
        variant="caption"
      >
        <InfoIcon sx={{ fontSize: 12 }} /> {getProcDescription(proc.name)}
      </Typography>
    </Box>
  );
};

// --- Akıllı Metrik ve Güvenlik Açıklayıcı Kartı ---
const MetricCard = ({
  title,
  value,
  description,
  color,
  isWarning = false,
}) => {
  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1,
        bgcolor: "rgba(234,240,248,0.03)",
        borderRadius: 2,
        borderLeft: "3px solid",
        borderColor: isWarning ? "error.main" : color,
        "&:last-child": { mb: 0 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Typography sx={{ color: "text.primary", fontWeight: "bold" }} variant="caption">
          {title}
        </Typography>
        <Typography sx={{ color: isWarning ? "error.light" : "text.secondary", fontFamily: "monospace", fontWeight: "bold" }}
          variant="caption"
        >
          {value}
        </Typography>
      </Box>
      <Typography sx={{ color: "text.disabled", display: "flex", alignItems: "center", gap: 0.5 }}
        variant="caption"
      >
        {isWarning ? (
          <WarningIcon sx={{ fontSize: 12, color: "error.main" }} />
        ) : (
          <InfoIcon sx={{ fontSize: 12 }} />
        )}
        {description}
      </Typography>
    </Box>
  );
};

// -------------------------------------------------------------------------------------------------

export function SecurityCol({ stats, setDomainModalOpen }) {
  // Gelen tüm servisleri UP ve DOWN olarak filtreliyoruz
  const activeServices = (stats.services || []).filter(
    (s) => s.status === "UP",
  );
  const downServices = (stats.services || []).filter(
    (s) => s.status === "DOWN",
  );

  return (
    <Grid size={{ xs: 12, md: 4 }}>
      {/* --- YENİLENEN SERVİS BÖLÜMÜ (SCROLL DESTEKLİ) --- */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ color: "text.secondary", fontWeight: "900", letterSpacing: 1.5, display: "block", mb: 1 }}
          variant="overline"
        >
          İSKELET SERVİS DURUMLARI
        </Typography>

        {/* 1. AKTİF SERVİSLER KUTUSU */}
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: "rgba(16,185,129,0.05)",
            borderRadius: 2,
            border: "1px solid rgba(16,185,129,0.2)",
          }}
        >
          <Typography sx={{ color: "success.main", fontWeight: "bold", display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
            variant="caption"
          >
            <CheckCircleIcon sx={{ fontSize: 16 }} /> AKTİF ÇALIŞAN SERVİSLER (
            {activeServices.length})
          </Typography>

          {/* Scrollable Alan */}
          <Box
            sx={{
              maxHeight: 250,
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "rgba(16,185,129,0.3)",
                borderRadius: 2,
              },
            }}
          >
            <Stack spacing={1}>
              {activeServices.length > 0 ? (
                activeServices.map((s, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 1,
                      bgcolor: "rgba(234,240,248,0.02)",
                      borderRadius: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ color: "text.primary", fontWeight: "bold" }}
                        variant="caption"
                      >
                        {s.name}
                        {"    "}
                      </Typography>
                      <Typography sx={{ color: "text.secondary", display: "block", fontSize: "0.65rem" }}
                        variant="caption"
                      >
                        {getServiceDescription(s.name)}
                      </Typography>
                    </Box>
                    <Chip
                      label="UP"
                      size="small"
                      sx={{
                        bgcolor: "rgba(16,185,129,0.2)",
                        color: "#3ddc97",
                        fontWeight: "bold",
                        height: 20,
                        fontSize: "0.65rem",
                      }}
                    />
                  </Box>
                ))
              ) : (
                <Typography sx={{ color: "text.disabled" }} variant="caption">
                  Aktif servis bulunamadı.
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>

        {/* 2. PASİF/HATALI SERVİSLER KUTUSU */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: "rgba(244,63,94,0.05)",
            borderRadius: 2,
            border: "1px solid rgba(244,63,94,0.2)",
          }}
        >
          <Typography sx={{ color: "error.main", fontWeight: "bold", display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
            variant="caption"
          >
            <CancelIcon sx={{ fontSize: 16 }} /> ÇÖKEN / HATALI SERVİSLER (
            {downServices.length})
          </Typography>

          {/* Scrollable Alan */}
          <Box
            sx={{
              maxHeight: 150,
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "rgba(244,63,94,0.3)",
                borderRadius: 2,
              },
            }}
          >
            <Stack spacing={1}>
              {downServices.length > 0 ? (
                downServices.map((s, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 1,
                      bgcolor: "rgba(234,240,248,0.02)",
                      borderRadius: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ color: "error.light", fontWeight: "bold" }}
                        variant="caption"
                      >
                        {s.name}
                      </Typography>
                      <Typography sx={{ color: "text.secondary", display: "block", fontSize: "0.65rem" }}
                        variant="caption"
                      >
                        {getServiceDescription(s.name)}
                      </Typography>
                    </Box>
                    <Chip
                      label="FAILED"
                      size="small"
                      sx={{
                        bgcolor: "rgba(244,63,94,0.2)",
                        color: "#ff6b81",
                        fontWeight: "bold",
                        height: 20,
                        fontSize: "0.65rem",
                      }}
                    />
                  </Box>
                ))
              ) : (
                <Typography sx={{ color: "text.disabled" }} variant="caption">
                  Tüm izlenen servisler sorunsuz çalışıyor.
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* --- SSL BÖLÜMÜ --- */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography sx={{ color: "secondary.main", fontWeight: "900", letterSpacing: 1.5, display: "flex", alignItems: "center", gap: 1 }}
            variant="overline"
          >
            <WebIcon fontSize="small" /> SSL SERTİFİKALARI
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            sx={{ borderRadius: 2, py: 0, fontSize: "0.7rem" }}
            onClick={() => setDomainModalOpen(true)}
          >
            Düzenle
          </Button>
        </Box>
        <Stack sx={{ mt: 1 }} spacing={1.5}>
          {stats.ssl_reports && stats.ssl_reports.length > 0 ? (
            stats.ssl_reports.map((ssl, i) => (
              <LogLine
                key={i}
                title={ssl.domain}
                val={ssl.valid ? `${ssl.daysRemaining} Gün` : "Geçersiz"}
                isWarning={!ssl.valid || ssl.daysRemaining < 15}
              />
            ))
          ) : (
            <Typography sx={{ color: "text.disabled" }} variant="caption">
              İzlenen domain yok.
            </Typography>
          )}
        </Stack>
      </Box>

      {/* --- GÜVENLİK BÖLÜMÜ --- */}
      <Box>
        <Typography sx={{ color: "primary.light", fontWeight: "900", letterSpacing: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          variant="overline"
        >
          <ShieldIcon fontSize="small" /> GÜVENLİK TEHDİTLERİ
        </Typography>
        <Typography sx={{ color: "text.secondary", display: "block", mb: 1.5 }}
          variant="caption"
        >
          Sistem ağına yapılan ihlaller ve trafik analizleri:
        </Typography>

        <Box>
          <Accordion
            disableGutters
            sx={{
              bgcolor: "rgba(0,0,0,0.2)",
              border: "1px solid rgba(234,240,248,0.05)",
              borderRadius: "12px !important",
              mb: 1,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
            >
              <Typography sx={{ color: "error.light", fontWeight: "bold" }}
                variant="caption"
              >
                🔥 Anlık Şüpheli Trafik (Bugün)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Stack spacing={0}>
                {stats.top_ips && stats.top_ips.length > 0 ? (
                  stats.top_ips
                    .slice(0, 3)
                    .map((ip, i) => (
                      <MetricCard
                        key={i}
                        title={`IP Adresi: ${ip.ip}`}
                        value={`${ip.count} İstek`}
                        description={
                          ip.count > 100
                            ? "Olası DDoS/Botnet saldırısı! Nginx veya Firewall (UFW) üzerinden bu IP'yi derhal engelleyin."
                            : "Normal sınırlarda anlık bağlantı."
                        }
                        color="primary.main"
                        isWarning={ip.count > 100}
                      />
                    ))
                ) : (
                  <Typography sx={{ color: "text.disabled" }} variant="caption">
                    Sıradışı bağlantı trafiği algılanmadı.
                  </Typography>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            sx={{
              bgcolor: "rgba(0,0,0,0.2)",
              border: "1px solid rgba(234,240,248,0.05)",
              borderRadius: "12px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
            >
              <Typography sx={{ color: "warning.light", fontWeight: "bold" }}
                variant="caption"
              >
                🛡️ Yetkisiz Erişim Denemeleri (Bu Hafta)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Stack spacing={0}>
                {stats.ssh_fails && stats.ssh_fails.length > 0 ? (
                  stats.ssh_fails.map((fail, i) => (
                    <MetricCard
                      key={i}
                      title={`${fail.ip}`}
                      value={`${fail.count} başarısız deneme`}
                      description="Son 1 saatte bu adresten yanlış şifreyle SSH girişi denendi. Eşiği aşarsa Savunma motoru adresi otomatik engeller."
                      color="warning.main"
                      isWarning={true}
                    />
                  ))
                ) : (
                  <Typography sx={{ color: "text.disabled" }} variant="caption">
                    Yetkisiz giriş (SSH) ihlali bulunmuyor.
                  </Typography>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </Grid>
  );
}

// -------------------------------------------------------------------------------------------------

export function ResourceCol({ stats }) {
  const procs = stats.top_procs || [];
  const isDiskWarning = stats.disk_percent > 85;

  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ color: "warning.main", fontWeight: "900", letterSpacing: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          variant="overline"
        >
          <DataUsageIcon fontSize="small" /> KAYNAK TÜKETİMİ
        </Typography>
        <Typography sx={{ color: "text.secondary", display: "block", mb: 1.5 }}
          variant="caption"
        >
          Sunucuyu anlık olarak en çok yoran 10 süreç:
        </Typography>

        <Stack spacing={0}>
          {procs.length > 0 ? (
            <Accordion
              disableGutters
              sx={{
                bgcolor: "rgba(0,0,0,0.2)",
                border: "1px solid rgba(234,240,248,0.05)",
                borderRadius: "12px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
              >
                <Typography sx={{ color: "warning.light", fontWeight: "bold" }}
                  variant="caption"
                >
                  ⚙️ Aktif Süreçler (Top 10)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {procs.map((proc, i) => (
                  <ProcCard key={i} proc={proc} />
                ))}
              </AccordionDetails>
            </Accordion>
          ) : (
            <Typography sx={{ color: "text.disabled" }} variant="caption">
              Süreç verisi yok.
            </Typography>
          )}
        </Stack>
      </Box>

      {/* --- DİSK VE AĞ DETAYLARI --- */}
      <Box>
        <Typography sx={{ color: "info.main", fontWeight: "900", letterSpacing: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          variant="overline"
        >
          <StorageIcon fontSize="small" /> DİSK VE AĞ DETAYLARI
        </Typography>
        <Typography sx={{ color: "text.secondary", display: "block", mb: 1.5 }}
          variant="caption"
        >
          Depolama kapasitesi ve anlık bant genişliği:
        </Typography>

        <Box>
          <Accordion
            disableGutters
            sx={{
              bgcolor: "rgba(0,0,0,0.2)",
              border: "1px solid rgba(234,240,248,0.05)",
              borderRadius: "12px !important",
              mb: 1,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
            >
              <Typography sx={{ color: "info.light", fontWeight: "bold" }}
                variant="caption"
              >
                💾 Depolama Birimleri
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <MetricCard
                title="KÖK DİZİN (/)"
                value={`${stats.disk_used} / ${stats.disk_total} GB`}
                description={`Ana disk doluluk oranı (%${stats.disk_percent}). Sistem çökmelerini önlemek için %85 altında olmalıdır.`}
                color="info.main"
                isWarning={isDiskWarning}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            sx={{
              bgcolor: "rgba(0,0,0,0.2)",
              border: "1px solid rgba(234,240,248,0.05)",
              borderRadius: "12px !important",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
            >
              <Typography sx={{ color: "secondary.light", fontWeight: "bold" }}
                variant="caption"
              >
                🌐 Bant Genişliği (Ağ Trafiği)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <MetricCard
                title="INBOUND (RX)"
                value={`${stats.net_rx} MB`}
                description="Sunucuya dışarıdan gelen (indirilen) toplam trafik hacmi."
                color="secondary.main"
              />
              <MetricCard
                title="OUTBOUND (TX)"
                value={`${stats.net_tx} MB`}
                description="Sunucunun dışarıya gönderdiği (yüklediği) toplam trafik hacmi."
                color="secondary.main"
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </Grid>
  );
}

// -------------------------------------------------------------------------------------------------

export function AppCol({ stats, server, navigate, setSqlModalOpen }) {
  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography sx={{ color: "success.main", fontWeight: "900", letterSpacing: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          variant="overline"
        >
          <MemoryIcon fontSize="small" /> UYGULAMALAR (PM2)
        </Typography>
      </Box>
      <Grid container spacing={1} mb={3}>
        <Grid size={{ xs: 4 }}>
          <Button
            fullWidth
            size="small"
            variant="contained"
            onClick={() =>
              navigate("/terminal", {
                state: { serverId: server.id, serverName: server.name },
              })
            }
            sx={{ borderRadius: 2, py: 1, fontWeight: "bold" }}
          >
            SSH
          </Button>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 2,
              py: 1,
              borderColor: "rgba(234,240,248,0.1)",
              color: "text.primary",
            }}
            onClick={() => navigate("/databases")}
          >
            SQL
          </Button>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => setSqlModalOpen(true)}
            sx={{ borderRadius: 2, py: 1, fontWeight: "bold" }}
          >
            GEZGİN
          </Button>
        </Grid>
      </Grid>
      <Stack spacing={1.5}>
        {stats.pm2_apps && stats.pm2_apps.length > 0 ? (
          stats.pm2_apps.map((app, i) => <Pm2AppCard key={i} app={app} />)
        ) : (
          <Box
            sx={{
              p: 4,
              border: "1px dashed rgba(234,240,248,0.1)",
              borderRadius: 3,
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: "text.secondary" }} variant="body2">
              Süreçler izlenmiyor.
            </Typography>
          </Box>
        )}
      </Stack>
    </Grid>
  );
}
